import { ChevronDown, TrendingUp } from "lucide-react";
import { C } from "../../theme/tokens";
import { FOREX_PAIRS, OTC_PAIRS, formatPairLabel } from "../../data/pairs";


/* Blurred loading veil — sits on top of `children` while `active` is
   true, so the screen underneath it (already-rendered layout, stale
   chart, previous list, etc.) stays visible-but-blurred instead of
   the whole tab flashing to blank/empty. Used for any view whose
   data comes from one or more in-flight requests (REST and/or
   sockets): keep it mounted, flip `active` off only once every one
   of those requests has actually resolved. `children` still renders
   underneath at all times, so nothing has to wait for the overlay to
   unmount to start receiving data. */
export function LoadingOverlay({ active, label, children, style }) {
  return (
    <div
      style={{
        position: "relative",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        ...style,
      }}
    >
      {children}
      {active && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            background: "rgba(2,4,2,0.55)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            borderRadius: "inherit",
            zIndex: 5,
            transition: "opacity .15s ease",
          }}
        >
          <div
            style={{
              position: "relative",
              width: 58,
              height: 58,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: 18,
                border: `1px solid ${C.greenBright}55`,
                background: `linear-gradient(135deg, ${C.deep} 0%, ${C.green} 100%)`,
                boxShadow: `0 0 24px ${C.greenBright}25`,
                transform: "rotate(45deg)",
                animation: "loadingLogoPulse 2.2s ease-in-out infinite",
              }}
            />
            <TrendingUp size={24} color={C.primary} strokeWidth={2.5} style={{ position: "relative" }} />
          </div>
          <div
            style={{
              color: C.primary,
              fontSize: 15,
              fontWeight: 700,
              letterSpacing: "0.03em",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            JoTrade Pro
          </div>
          {label && (
            <div
              style={{
                color: C.secondary,
                fontSize: 13,
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 600,
              }}
            >
              {label}
            </div>
          )}
          <style>{`
            @keyframes loadingLogoPulse {
              0%, 100% { transform: rotate(45deg) scale(1); opacity: .86; }
              50% { transform: rotate(45deg) scale(1.07); opacity: 1; }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}

export function Hairline() {
  return <div style={{ height: 1, background: C.hairline, width: "100%" }} />;
}

export function SectionHeader({ eyebrow, title, sub, wide }) {
  return (
    <div style={{ padding: wide ? "8px 0 22px" : "22px 20px 14px" }}>
      {eyebrow && (
        <div
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: "0.12em",
            color: C.violetBright,
            marginBottom: 6,
            textTransform: "uppercase",
          }}
        >
          {eyebrow}
        </div>
      )}
      <h1
        style={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: wide ? 32 : 24,
          fontWeight: 600,
          color: C.primary,
          margin: 0,
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </h1>
      {sub && (
        <p style={{ color: C.secondary, fontSize: wide ? 15 : 13.5, margin: "6px 0 0" }}>
          {sub}
        </p>
      )}
    </div>
  );
}

/* Sparkline used as the small recurring signature motif — a gradient
   stroke that travels from green into violet, echoing the app's two
   brand colors blending into one line rather than staying separate. */
export function Sparkline({ w = 120, h = 34 }) {
  const pts = [4, 10, 7, 16, 12, 22, 18, 28, 24, 33];
  const step = w / (pts.length - 1);
  const d = pts
    .map((p, i) => `${i === 0 ? "M" : "L"}${i * step},${h - p}`)
    .join(" ");
  const gid = "sparkGrad";
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} fill="none">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2={w} y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={C.violet} />
          <stop offset="1" stopColor={C.violetBright} />
        </linearGradient>
      </defs>
      <path d={d} stroke={`url(#${gid})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={w} cy={h - pts[pts.length - 1]} r="3.5" fill={C.violetBright} />
    </svg>
  );
}

/* Ticket-shaped card: a nod to a trade ticket — one clipped corner
   (echoing the reference design's balance panel) with a soft glow
   underneath, used once for the balance, so it earns its
   distinctiveness rather than becoming a generic card everywhere. */
export function TicketCard({ children }) {
  return (
    <div
      style={{
        position: "relative",
        clipPath: "polygon(0 0, calc(100% - 26px) 0, 100% 26px, 100% 100%, 0 100%)",
        background: `linear-gradient(160deg, ${C.panel2}, ${C.panel1} 60%)`,
        border: `1px solid ${C.hairline}`,
        padding: "22px 22px 20px",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 26,
          height: 26,
          background: `linear-gradient(135deg, transparent 49%, ${C.greenBright}22 50%)`,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `radial-gradient(120% 60% at 15% 0%, ${C.greenBright}12, transparent 55%)`,
          pointerEvents: "none",
        }}
      />
      {children}
    </div>
  );
}

/* Dashed hairline used inside TicketCard-style panels to separate
   sections (e.g. balance from Pocket ID), echoing the reference
   design's perforated ledger seam. */
export function DashedDivider() {
  return (
    <div
      style={{
        height: 1,
        margin: "18px 0 14px",
        backgroundImage: `repeating-linear-gradient(90deg, ${C.hairline} 0 6px, transparent 6px 11px)`,
      }}
    />
  );
}

export function Chip({ active, children, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "9px 16px",
        borderRadius: 10,
        border: `1px solid ${active ? C.violetBright : C.hairline}`,
        background: active
          ? `linear-gradient(120deg, ${C.violet} 0%, ${C.violetBright} 100%)`
          : "transparent",
        color: active ? "#0b0d13" : C.secondary,
        fontFamily: "'Space Grotesk', sans-serif",
        fontWeight: 600,
        fontSize: 13.5,
        cursor: "pointer",
        transition: "all .15s ease",
      }}
    >
      {children}
    </button>
  );
}

/* Horizontal pill strip for picking one of several short timeframe
   codes (S5, M1, H1, ...) — a single dark rounded bar holding
   compact buttons, the active one lit up. Scrolls horizontally on
   narrow screens instead of wrapping, so the bar stays one clean row.
   `accent` is optional — omit it for the original neutral white
   active pill, or pass a section's own color (e.g. Scanner's teal)
   so the control picks up that section's identity. */
export function TimeframeSelect({ value, onChange, options, accent }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        background: C.panel1,
        border: `1px solid ${C.hairline}`,
        borderRadius: 14,
        padding: 4,
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
      }}
    >
      {options.map((opt) => {
        const active = opt === value;
        return (
          <button
            key={opt}
            onClick={() => onChange(opt)}
            style={{
              flex: "0 0 auto",
              padding: "8px 14px",
              borderRadius: 10,
              border: "none",
              background: active ? (accent || C.primary) : "transparent",
              color: active ? "#05060a" : C.secondary,
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 600,
              fontSize: 12.5,
              cursor: "pointer",
              transition: "all .15s ease",
              whiteSpace: "nowrap",
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

/* Native <select> styled to match the app — used for pair/timeframe
   pickers on Live Charts so they read as compact controls rather than
   a wide row of chips, leaving more room for the chart itself.
   `compact` trims padding/font so it can sit inline in a chart header
   (small pill next to the pair name) instead of a full-width field. */
export function Select({ value, onChange, options, compact = false }) {
  return (
    <div style={{ position: "relative", display: compact ? "inline-block" : "block", flex: compact ? "0 0 auto" : 1 }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: compact ? "auto" : "100%",
          appearance: "none",
          WebkitAppearance: "none",
          MozAppearance: "none",
          background: compact ? C.panel1 : C.panel1,
          border: `1px solid ${C.hairline}`,
          borderRadius: compact ? 8 : 12,
          color: C.primary,
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: compact ? 700 : 600,
          fontSize: compact ? 12.5 : 14.5,
          padding: compact ? "6px 26px 6px 10px" : "12px 38px 12px 14px",
          cursor: "pointer",
          outline: "none",
        }}
      >
        {options.map((o) => (
          <option key={o} value={o} style={{ background: C.panel1, color: C.primary }}>
            {o}
          </option>
        ))}
      </select>
      <ChevronDown
        size={compact ? 13 : 16}
        color={C.mutedB}
        style={{
          position: "absolute",
          right: compact ? 8 : 14,
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

/* Grouped native <select> for the full pair instrument list — splits
   Forex vs. OTC into <optgroup>s so the long list stays scannable,
   and renders human-readable labels ("EUR/USD") while keeping the raw
   feed code as the underlying value.

   `variant="minimal"` strips the boxed field chrome (background,
   border, padding) down to bold inline text + a small chevron, so the
   picker can live directly inside a chart's own header bar instead of
   reading as a separate form control floated on top of it. */
export function PairSelect({ value, onChange, forexLabel, otcLabel, variant = "boxed" }) {
  const minimal = variant === "minimal";
  return (
    <div style={{ position: "relative", display: minimal ? "inline-block" : "block", width: minimal ? "auto" : "100%" }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={
          minimal
            ? {
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                background: "transparent",
                border: "none",
                color: C.primary,
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 700,
                fontSize: 14.5,
                padding: "0 20px 0 0",
                cursor: "pointer",
                outline: "none",
              }
            : {
                width: "100%",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                background: C.panel1,
                border: `1px solid ${C.hairline}`,
                borderRadius: 12,
                color: C.primary,
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 700,
                fontSize: 15,
                padding: "14px 38px 14px 14px",
                cursor: "pointer",
                outline: "none",
              }
        }
      >
        <optgroup label={forexLabel} style={{ background: C.panel1, color: C.secondary }}>
          {FOREX_PAIRS.map((p) => (
            <option key={p} value={p} style={{ background: C.panel1, color: C.primary }}>
              {formatPairLabel(p)}
            </option>
          ))}
        </optgroup>
        <optgroup label={otcLabel} style={{ background: C.panel1, color: C.secondary }}>
          {OTC_PAIRS.map((p) => (
            <option key={p} value={p} style={{ background: C.panel1, color: C.primary }}>
              {formatPairLabel(p)}
            </option>
          ))}
        </optgroup>
      </select>
      <ChevronDown
        size={minimal ? 15 : 17}
        color={C.violetBright}
        style={{
          position: "absolute",
          right: minimal ? 0 : 14,
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

/* Shared brand CTA — green/sage gradient by default (auth flows,
   Signals). Pass `fromColor`/`toColor`/`shadowColor` to give a
   specific section its own accent (e.g. Scanner's teal) without
   touching every other screen that uses this same button. */
export function PrimaryButton({ children, onClick, disabled, fromColor, toColor, shadowColor }) {
  const from = fromColor || C.greenBright;
  const to = toColor || C.green;
  const shadow = shadowColor || C.greenBright;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        padding: "15px 18px",
        borderRadius: 14,
        border: "none",
        background: disabled
          ? C.mutedA
          : `linear-gradient(120deg, ${from} 0%, ${to} 100%)`,
        color: disabled ? C.mutedB : "#0b0d13",
        fontFamily: "'Space Grotesk', sans-serif",
        fontWeight: 700,
        fontSize: 15,
        letterSpacing: "-0.01em",
        cursor: disabled ? "not-allowed" : "pointer",
        boxShadow: disabled ? "none" : `0 8px 26px -8px ${shadow}99`,
      }}
    >
      {children}
    </button>
  );
}